import { useTranslation } from 'react-i18next'
import type { CSSProperties } from 'react'
import { Wrench } from 'lucide-react'

import { cn } from '@/lib/utils.ts'
import { getTopBannerOffsetPx } from '@/routes/root/view/topBannerLayout.ts'

type MaintenanceOverrideBannerProps = {
  visible: boolean
  bannerIndex?: number
}

/**
 * Persistent banner shown to TEAM members (isSpecialUser) while a maintenance
 * window is open — a reminder that regular users are locked out and they have
 * override access. Regular users get the full-screen MaintenanceScreen instead.
 *
 * Visibility is computed by RootLayout (so the banner-stack offset math stays in
 * lockstep) and passed in as `visible`, mirroring BackgroundSyncIndicator.
 */
export default function MaintenanceOverrideBanner({
  visible,
  bannerIndex = 0,
}: MaintenanceOverrideBannerProps) {
  const { t } = useTranslation()

  if (!visible) return null

  return (
    <div
      role="status"
      className={cn(
        'fixed left-0 right-0 z-[110]',
        'top-[calc(var(--safe-area-inset-top)+var(--top-banner-offset,0px))]',
        'md:top-[var(--top-banner-offset,0px)]',
        'md:left-[var(--sidebar-width)]',
      )}
      style={
        {
          '--top-banner-offset': `${getTopBannerOffsetPx({ bannerIndex })}px`,
        } as CSSProperties
      }
    >
      <div className="flex h-8 items-center justify-center gap-2 border-b border-violet-200 bg-violet-50 px-3 dark:border-violet-900 dark:bg-violet-950/50">
        <Wrench className="size-3.5 shrink-0 text-violet-600 dark:text-violet-400" />
        <span className="truncate text-xs font-medium text-violet-800 dark:text-violet-200">
          {t('maintenance.banner.staffActive')}
        </span>
      </div>
    </div>
  )
}
